<template>
  <div data-testid="pm-pending-closures">
    <div class="flex justify-end gap-2 mb-4">
      <Button
        :label="$t('ProjectManagement.pendingClosures.refresh')"
        icon="pi pi-refresh"
        severity="secondary"
        :loading="loading"
        data-testid="pm-pending-closures-refresh"
        @click="load"
      />
    </div>

    <Message v-if="error" severity="error" :closable="false" class="mb-4">
      {{ error }}
    </Message>

    <div v-if="loading" class="flex items-center justify-center py-16 text-[var(--color-disabled)]">
      <i class="pi pi-spin pi-spinner mr-2" />
      {{ $t('ProjectManagement.pendingClosures.loading') }}
    </div>

    <div
      v-else-if="!items.length"
      class="surface-card border-round p-8 text-center text-[var(--color-disabled)]"
      data-testid="pm-pending-closures-empty"
    >
      <i class="pi pi-shield text-3xl mb-3 block" />
      <div class="font-medium text-[var(--color-dark)]">
        {{ $t('ProjectManagement.pendingClosures.emptyTitle') }}
      </div>
      <div class="text-sm mt-1">{{ $t('ProjectManagement.pendingClosures.emptyHint') }}</div>
    </div>

    <DataTable v-else :value="items" data-key="id" striped-rows data-testid="pm-pending-closures-table">
      <Column field="name" :header="$t('ProjectManagement.pendingClosures.columns.project')" />
      <Column :header="$t('ProjectManagement.pendingClosures.columns.requestedAt')">
        <template #body="{ data }">
          {{ formatDate(data.requestedAt) }}
        </template>
      </Column>
      <Column field="requestedByName" :header="$t('ProjectManagement.pendingClosures.columns.requestedBy')" />
      <Column :header="$t('ProjectManagement.pendingClosures.columns.daysOpen')">
        <template #body="{ data }">
          <span :class="data.daysOpen > 30 ? 'text-[var(--color-error)] font-medium' : ''">
            {{ data.daysOpen }}
          </span>
        </template>
      </Column>
      <Column :header="$t('ProjectManagement.pendingClosures.columns.actions')">
        <template #body="{ data }">
          <div class="flex gap-2 justify-end">
            <Button
              :label="$t('ProjectManagement.pendingClosures.approve')"
              class="btn-primary-accent"
              unstyled
              size="small"
              :loading="actingId === data.id"
              data-testid="pm-pending-approve"
              @click="approve(data)"
            />
            <Button
              :label="$t('ProjectManagement.pendingClosures.reactivate')"
              severity="secondary"
              size="small"
              :loading="actingId === data.id"
              data-testid="pm-pending-reactivate"
              @click="reactivate(data)"
            />
          </div>
        </template>
      </Column>
    </DataTable>
  </div>
</template>

<script>
import Button from 'primevue/button';
import Column from 'primevue/column';
import DataTable from 'primevue/datatable';
import Message from 'primevue/message';
import { useToast } from 'primevue/usetoast';
import pmProjectSettingsService from '@/services/pmProjectSettingsService';

export default {
  name: 'PendingClosuresPanel',
  components: { Button, Column, DataTable, Message },
  emits: ['changed'],
  setup() {
    return { toast: useToast() };
  },
  data() {
    return {
      loading: false,
      error: '',
      items: [],
      actingId: null,
    };
  },
  mounted() {
    this.load();
  },
  methods: {
    formatDate(value) {
      if (!value) return '—';
      return new Date(value).toLocaleDateString(undefined, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    },
    async load() {
      this.loading = true;
      this.error = '';
      try {
        const data = await pmProjectSettingsService.listPendingClose();
        this.items = data.items || [];
      } catch (error) {
        this.error = error?.response?.data?.message
          || this.$t('ProjectManagement.pendingClosures.error');
        this.items = [];
      } finally {
        this.loading = false;
      }
    },
    async approve(row) {
      this.actingId = row.id;
      try {
        await pmProjectSettingsService.approveClosure(row.id);
        this.toast.add({
          severity: 'success',
          summary: this.$t('ProjectManagement.pendingClosures.toastApproved'),
          life: 3000,
        });
        await this.load();
        this.$emit('changed');
      } catch (error) {
        this.toast.add({
          severity: 'error',
          summary: error?.response?.data?.message
            || this.$t('ProjectManagement.pendingClosures.error'),
          life: 4000,
        });
      } finally {
        this.actingId = null;
      }
    },
    async reactivate(row) {
      this.actingId = row.id;
      try {
        await pmProjectSettingsService.reactivate(row.id);
        this.toast.add({
          severity: 'success',
          summary: this.$t('ProjectManagement.pendingClosures.toastReactivated'),
          life: 3000,
        });
        await this.load();
        this.$emit('changed');
      } catch (error) {
        this.toast.add({
          severity: 'error',
          summary: error?.response?.data?.message
            || this.$t('ProjectManagement.pendingClosures.error'),
          life: 4000,
        });
      } finally {
        this.actingId = null;
      }
    },
  },
};
</script>
